import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAgentKeys, useCreateAgentKey, useRevokeAgentKey } from "../../hooks/useWorkspaceMembers";
import { AgentConnectHelp } from "./AgentConnectHelp";
import { AgentKeyList } from "./AgentKeyList";
import { AgentKeyReveal } from "./AgentKeyReveal";

interface AgentKeysSectionProps {
  workspaceId: string;
  agentUserId: string;
}

/** An AI agent's keys: list, Create New Key (shown once), Revoke. Admin-only on the server; the caller renders it for admins. */
export function AgentKeysSection({ workspaceId, agentUserId }: AgentKeysSectionProps) {
  const { t } = useTranslation();
  const { data: keys = [], error: loadError, isLoading } = useAgentKeys(workspaceId, agentUserId, true);
  const createKey = useCreateAgentKey(workspaceId);
  const revoke = useRevokeAgentKey(workspaceId, agentUserId);

  const handleCreate = () => {
    createKey.mutate(agentUserId, {
      onError: (err) => console.error("createAgentKey failed", { workspaceId, userId: agentUserId, code: (err as { code?: string }).code }),
    });
  };

  const handleRevoke = (keyId: string) => {
    revoke.mutate(keyId, {
      onError: (err) => console.error("revokeAgentKey failed", { workspaceId, userId: agentUserId, keyId, code: (err as { code?: string }).code }),
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="text-lg font-medium text-foreground">{t("agent.keys.title")}</h3>
        {!createKey.data && (
          <button
            type="button"
            onClick={handleCreate}
            disabled={createKey.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {createKey.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {createKey.isPending ? t("agent.keys.creating") : t("agent.keys.create")}
          </button>
        )}
      </div>

      {createKey.isError && <p role="alert" className="mb-3 text-sm text-red-600">{t("agent.keys.createFailed")}</p>}
      {createKey.data && <AgentKeyReveal keyValue={createKey.data.key} onDone={() => createKey.reset()} />}

      <div className="mt-4 space-y-4">
        {loadError ? (
          <p role="alert" className="text-sm text-red-600">{t("agent.keys.loadFailed")}</p>
        ) : isLoading ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : (
          <AgentKeyList keys={keys} revokingId={revoke.isPending ? (revoke.variables ?? "") : null} onRevoke={handleRevoke} />
        )}
        {revoke.isError && <p role="alert" className="text-sm text-red-600">{t("agent.keys.revokeFailed")}</p>}
        {!createKey.data && <AgentConnectHelp />}
      </div>
    </div>
  );
}
